<template>
  <div class="registration theme-group-3">
    <app-page-wrapper-grid class="registration__grid">
      <div class="registration__container"></div>
      <!-- Back button -->
      <div class="iw-layout-grid__row p-l-16 p-b-8 p-t-24">
        <div class="iw-layout-grid__col--span-12">
          <back-button />
        </div>
      </div>

      <!-- Form -->
      <div class="iw-layout-grid__row p-16">
        <div class="iw-layout-grid__col--span-4 iw-layout-grid__col--offset-4-desktop">
          <form id="sigUpForm" class="registration__form">
            <h3 class="iw-page-title m-b-40">Register</h3>
            <template v-if="isFormSubmitted && (v$.$invalid || responseWithErrors)">
              <app-notifier
                v-for="(message, index) in errors"
                :key="index"
                class="registration__error"
                type="failed"
                :data="message"
              />
            </template>
            <div class="iw-body-text-small m-l-auto">*Required Information</div>
            <app-input
              v-model="v$.user.firstName.$model"
              label="First Name"
              :has-error="v$.user.firstName.$error && isFormSubmitted"
              class="registration__input"
              required
            />
            <app-input
              v-model="v$.user.lastName.$model"
              label="Last Name"
              :has-error="v$.user.lastName.$error && isFormSubmitted"
              class="registration__input"
              required
            />
            <app-input
              v-model="v$.user.phone.$model"
              label="Phone"
              :has-error="v$.user.phone.$error && isFormSubmitted"
              mask="(###) ###-####"
              class="registration__input"
              :error-message="v$.user.phone.$error && isFormSubmitted ? validatorsCollection.get('phone').errorMessage : ''"
              required
            />
            <email-guidance-text class="m-t-10 m-b-15" />
            <app-input
              v-model="v$.user.email.$model"
              label="Email"
              :has-error="v$.user.email.$error && isFormSubmitted"
              class="registration__input"
              :error-message="v$.user.email.$error && isFormSubmitted ? validatorsCollection.get('email').errorMessage : ''"
              required
            />
            <app-input
              v-model="v$.user.password.$model"
              type="password"
              :has-error="v$.user.password.$error && isFormSubmitted"
              class="registration__input"
              label="Password"
              :error-message="passwordError"
              required
            />

            <!--  Privacy Policy -->
            <p class="iw-body-text-small registration__policy-text">
              Information captured will only be used for updates to your order. By entering your phone number here you agree to
              receive SMS order updates. For more information see our
              <button class="registration__tclink" @click.stop.prevent="openPrivacyPolicyModal">Privacy Policy</button>
            </p>
            <app-button text="Continue" :aria-label="'Continue'" :handler="submit" />

            <!--  Bottom text & login link -->
            <p class="iw-body-text-small registration__bottom-text">
              Already have an account?
              <app-link
                :route-to="{
                  name: ROUTE_NAMES.LOGIN,
                  params: {
                    target: $route.name || $route.params.target,
                    vendor: getActiveVendor ? getActiveVendor.id : null
                  }
                }"
                class="registration__link"
                >Login</app-link
              >
            </p>
          </form>
        </div>
      </div>
    </app-page-wrapper-grid>
  </div>
</template>

<!-- prettier-ignore -->
<script>

import { mapGetters, mapActions } from "vuex";
import useVuelidate from "@vuelidate/core";
import PrivacyPolicyModal from "@/components/modal/privacy-policy-modal/PrivacyPolicyModal";
import BackButton from "@/components/buttons/back-button/BackButton";
import EmailGuidanceText from "@/components/messages/EmailGuidanceText.vue";
import {
  DISPLAY,
  passwordValidations,
  phoneValidations,
  emailValidation,
  requiredValidation,
  ROUTE_NAMES,
  REQUIRED_ERR_MSG
} from "@/utils";

export const DEFAULT_BACK_ERROR_MSG = "Your account cannot be created at this time. Please try again later.";
export const ACCOUNT_EXISTS_BACK_ERROR_MSG =
  "Your email address or phone number is already associated with an account. Please login to continue.";

export default {
  name: "Registration",
  components: {
    BackButton,
    EmailGuidanceText
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevRoute = from;
    });
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      isFormSubmitted: false,
      responseWithErrors: false,
      user: {
        firstName: "",
        lastName: "",
        name: "",
        email: "",
        phone: "",
        password: ""
      },
      passwordError: "",
      errors: [],
      validatorsCollection: new Map(),
      prevRoute: null,
      ROUTE_NAMES
    };
  },
  validations: {
    user: {
      firstName: requiredValidation(),
      lastName: requiredValidation(),
      email: emailValidation(),
      password: passwordValidations(),
      phone: phoneValidations()
    }
  },
  computed: {
    ...mapGetters("user", ["getUserPhoneNumber"]),
    ...mapGetters("vendor", ["getActiveVendor"])
  },
  mounted() {
    this.validatorsCollection
      .set("firstName", { validator: this.v$.user.firstName, errorMessage: "" })
      .set("lastName", { validator: this.v$.user.lastName, errorMessage: "" })
      .set("email", {
        validator: this.v$.user.email,
        errorMessage: "Please enter a valid email address"
      })
      .set("phone", {
        validator: this.v$.user.phone,
        errorMessage: "Please enter a valid 10-digit mobile number"
      })
      .set("password", {
        validator: this.v$.user.password,
        errorMessage:
          "Your password must be at least 8 characters long, contain both uppercase and lowercase letters, and at least one number and one special character"
      });

    this.user.phone = this.getUserPhoneNumber;
  },
  methods: {
    ...mapActions("user", ["registration", "authorization"]),
    async submit() {
      this.isFormSubmitted = false;
      this.responseWithErrors = false;
      this.errors.length = 0;
      this.passwordError = "";

      this.v$.$touch();
      this.checkInvalidFields();

      if (!this.v$.$invalid) {
        try {
          const userData = this.createUserData();
          await this.registration(userData);
        } catch (error) {
          this.isFormSubmitted = true;
          this.responseWithErrors = true;

          if (error === 409) {
            this.errors.push(ACCOUNT_EXISTS_BACK_ERROR_MSG);
            return;
          }

          this.errors.push(DEFAULT_BACK_ERROR_MSG);
          return;
        }

        this.pushNavigation();
      }
      this.isFormSubmitted = true;
    },
    checkInvalidFields() {
      this.validatorsCollection.forEach((item, key) => {
        if (item.validator.$invalid) {
          if (item.validator.required.$invalid) {
            if (!this.errors.includes(REQUIRED_ERR_MSG)) {
              this.errors.push(REQUIRED_ERR_MSG);
            }
          } else {
            if (key === "password") {
              this.passwordError = item.errorMessage;
            } else if (key !== "phone" && key !== "email" && key !== "password") {
              // <- IWA-2043: remove check when all fields are treated the same
              this.errors.push(item.errorMessage);
            }
          }
        }
      });
    },
    pushNavigation() {
      //TODO: For automation: Need to determine why target isn’t populated in selenium browser, is it a browser setting?
      if ("target" in this.$route.params && this.$route.params.target) {
        return this.$router.push({ name: this.$route.params.target });
      } else {
        return this.$router.push({ name: this.prevRoute?.name || ROUTE_NAMES.VENUE_CONTAINER });
      }
    },
    createUserData() {
      return {
        profile: {
          email: this.user.email,
          firstName: this.user.firstName,
          lastName: this.user.lastName,
          phone: this.user.phone.replace(/[^0-9]/g, "")
        },
        password: this.user.password
      };
    },
    openPrivacyPolicyModal() {
      this.$modal.show(
        PrivacyPolicyModal,
        {},
        window.innerWidth >= DISPLAY.minDesktopWidth
          ? {
              name: "PrivacyPolicyModal",
              height: "85%",
              width: "70%",
              adaptive: true,
              scrollable: true
            }
          : {
              name: "PrivacyPolicyModal",
              height: "100%",
              width: "100%",
              adaptive: true,
              scrollable: true
            }
      );
    }
  }
};
</script>
<style scoped lang="scss">
.error {
  text-transform: uppercase;
  color: $error-color;
  margin: 0 0 10px 0;
  padding: 1em 1em;
  font-size: 0.5em;
}

.registration {
  &__title {
    width: 100%;
    text-align: left;
  }

  &__form {
    display: flex;
    flex-direction: column;
    justify-content: left;
    align-items: flex-start;
  }

  &__address {
    display: flex;
  }

  &__link {
    font-size: 14px;
    margin-left: 5px;
    color: $group-3-color-4;
    &:hover {
      text-decoration: underline;
    }
  }

  &__bottom-text {
    margin: 27px 16px 32px 16px;
  }

  &__tclink {
    font-size: 14px;
    font-family: $group-3-font-family-1;
    color: $group-3-color-4;
    background: none;
    padding: 0;
    border: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
  &__terms {
    font-size: 13px;
  }

  &__input {
    margin-bottom: 12px !important;
  }

  &__policy-text {
    color: $group-3-color-3 !important;
    margin: 12px 16px 24px 16px;
  }

  &__error {
    padding-left: 16px;
    width: calc(100% - 16px);
  }
}
</style>
